import type { AudioVisualFrame } from '../domain/types'

const clamp = (value: number) => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0))

/** Measures the post-volume output spectrum. Bands describe audible energy, not inferred note events. */
export function analyseSpectrum(rms: number, spectrum: Float32Array, sampleRate: number, midiNotes: readonly number[]): AudioVisualFrame {
  if (!(rms > .0004) || !(sampleRate > 0) || spectrum.length === 0) return { level: 0, brightness: 0, notes: midiNotes.map(() => 0) }
  const binHz = sampleRate / (spectrum.length * 2)
  const level = clamp(Math.sqrt(rms * 5))
  const notes = midiNotes.map(midi => {
    const hz = 440 * 2 ** ((midi - 69) / 12)
    const center = Math.round(hz / binHz)
    // A narrow peak window tolerates vibrato and FFT bin boundaries without treating the whole spectrum as one note.
    let peak = -Infinity
    for (let bin = Math.max(1, center - 1); bin <= Math.min(spectrum.length - 1, center + 1); bin++) peak = Math.max(peak, spectrum[bin]!)
    return clamp((10 ** (peak / 20) - .00025) * 20) * level
  })
  let power = 0, highPower = 0
  for (let bin = 1; bin < spectrum.length; bin++) {
    const amplitude = Number.isFinite(spectrum[bin]) ? 10 ** (spectrum[bin]! / 20) : 0
    const energy = amplitude * amplitude
    power += energy
    if (bin * binHz > 1800) highPower += energy
  }
  return { level, brightness: clamp(power > 0 ? Math.sqrt(highPower / power) : 0), notes }
}
